import React, { useEffect, useMemo, useState } from 'react';
import useStagingAutoRefresh from './useStagingAutoRefresh';
import './StagingReviews.css';

const API = '/api/dashboards/admin/reviews';

function getToken() {
  try {
    return JSON.parse(localStorage.getItem('admin') || 'null')?.accessToken;
  } catch {
    return null;
  }
}

function dateLabel(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' :
    d.toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
}

function visibility(review) {
  if (review.deletedAt) return 'Deleted';
  if (Number(review.status) === 0) return 'Published';
  if (Number(review.status) === 2) return 'Hidden';
  return 'Other';
}

function csvCell(value) {
  let s = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  return '"' + s.replace(/"/g, '""') + '"';
}

export default function StagingReviews() {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [provider, setProvider] = useState('');
  const [rating, setRating] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('newest');
  const [selectedId, setSelectedId] = useState(null);
  const [metadata, setMetadata] = useState({history:[],reports:[]});
  const [moderationAction, setModerationAction] = useState('hide');
  const [reason, setReason] = useState('');
  const [explanation, setExplanation] = useState('');
  const [saving, setSaving] = useState(false);
  const [manualRefresh, setManualRefresh] = useState(0);
  const refreshKey = useStagingAutoRefresh();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const token = getToken();
        if (!token) throw new Error('Administrator session required.');

        const all = [];
        let total = null;

        for (let page = 1; page <= 1000; page++) {
          const response = await fetch(
            `${API}?page=${page}&perPage=100`,
            {
              headers: {
                'x-access-token': token,
                Accept: 'application/json'
              },
              cache: 'no-store'
            }
          );

          const json = await response.json();

          if (!response.ok || !json.success || !Array.isArray(json.reviews)) {
            throw new Error(json.message || 'Unable to load reviews.');
          }

          if (total === null) {
            total = Number(json.total);
            if (!Number.isSafeInteger(total) || total < 0) {
              throw new Error('Invalid review total returned by API.');
            }
          }

          all.push(...json.reviews);

          if (all.length >= total) break;
          if (!json.reviews.length) {
            throw new Error('Review pagination returned incomplete data.');
          }
        }

        if (all.length !== total) {
          throw new Error('Review list changed while loading. Will retry.');
        }

        if (!cancelled) {
          setReviews(all);
          setError('');
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Unable to refresh reviews.');
          setLoaded(true);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [refreshKey, manualRefresh]);

  useEffect(() => {
    let cancelled = false;
    const token = getToken();
    if (!token) return;
    fetch(`${API}/metadata`, {headers:{'x-access-token':token},cache:'no-store'})
      .then(r=>r.json()).then(data=>{
        if (!cancelled && data.success) setMetadata({history:data.history||[],reports:data.reports||[]});
      }).catch(()=>{});
    return ()=>{cancelled=true;};
  }, [refreshKey,manualRefresh]);

  async function submitModeration(kind) {
    if (!selected || !reason || saving) return;
    if (!window.confirm(`${kind === 'report' ? 'Flag' : moderationAction === 'hide' ? 'Hide' : 'Restore'} review #${selected.id}?`)) return;
    setSaving(true);
    try {
      const response = await fetch(`${API}/${encodeURIComponent(selected.id)}/${kind === 'report' ? 'report' : 'moderate'}`, {
        method:'POST',headers:{'Content-Type':'application/json','x-access-token':getToken()},
        body:JSON.stringify({action:moderationAction,reason,explanation})
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Request failed');
      setReason('');setExplanation('');setManualRefresh(v=>v+1);
      window.alert(kind === 'report' ? 'Review flagged.' : 'Review visibility updated.');
    } catch(e) { window.alert(e.message); }
    finally {setSaving(false);}
  }

  const counts = useMemo(() => ({
    all: reviews.length,
    published: reviews.filter(r => visibility(r) === 'Published').length,
    hidden: reviews.filter(r => visibility(r) === 'Hidden').length,
    reported: new Set(metadata.reports.map(r => String(r.reviewId))).size,
    other: reviews.filter(r =>
      !['Published', 'Hidden'].includes(visibility(r))
    ).length
  }), [reviews, metadata.reports]);

  const providers = useMemo(() =>
    [...new Set(reviews.map(r => r.providerName).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b)),
    [reviews]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    const result = reviews.filter(r => {
      const state = visibility(r);
      if (tab === 'reported' && !metadata.reports.some(report => String(report.reviewId) === String(r.id))) return false;
      if (tab === 'published' && state !== 'Published') return false;
      if (tab === 'hidden' && state !== 'Hidden') return false;
      if (tab === 'other' &&
          ['Published', 'Hidden'].includes(state)) return false;

      if (provider && r.providerName !== provider) return false;
      if (rating && Number(r.rating) !== Number(rating)) return false;

      const created = String(r.createdAt || '').slice(0, 10);
      if (dateFrom && (!created || created < dateFrom)) return false;
      if (dateTo && (!created || created > dateTo)) return false;

      if (q) {
        const haystack = [
          r.id, r.comment, r.clientName, r.providerName,
          r.bookingReference, r.bookingId, r.serviceName
        ].join(' ').toLowerCase();

        if (!haystack.includes(q)) return false;
      }

      return true;
    });

    result.sort((a, b) => {
      if (sort === 'rating-high') {
        return Number(b.rating || 0) - Number(a.rating || 0);
      }
      if (sort === 'rating-low') {
        return Number(a.rating || 0) - Number(b.rating || 0);
      }

      const diff = new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      return sort === 'oldest' ? -diff : diff;
    });

    return result;
  }, [reviews, metadata.reports, tab, search, provider, rating, dateFrom, dateTo, sort]);

  const selected = reviews.find(r => String(r.id) === String(selectedId));

  function reset() {
    setSearch('');
    setProvider('');
    setRating('');
    setDateFrom('');
    setDateTo('');
    setSort('newest');
  }

  function exportCsv() {
    const columns = [
      'Review ID', 'Client', 'Provider', 'Rating', 'Comment',
      'Review date', 'Appointment date', 'Booking reference',
      'Service', 'Visibility'
    ];

    const rows = filtered.map(r => [
      r.id, r.clientName, r.providerName, r.rating, r.comment,
      r.createdAt, r.bookingDate || r.dateOfBooking,
      r.bookingReference, r.serviceName, visibility(r)
    ]);

    const csv = [columns, ...rows]
      .map(row => row.map(csvCell).join(',')).join('\r\n');

    const blob = new Blob(['\uFEFF', csv], {
      type: 'text/csv;charset=utf-8'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'beautyspot-staging-reviews.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="bsrv-page">
      <div className="bsrv-inner">
        <div className="bsrv-heading">
          <div>
            <h1>Reviews</h1>
            <p>View and manage feedback left by Beauty Spot clients.</p>
          </div>
          <button className="bsrv-outline" onClick={exportCsv}
            disabled={!filtered.length}>
            Export filtered reviews ↗
          </button>
        </div>

        <div className="bsrv-notice">Current status as at {new Date().toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})}</div>

        <div className="bsrv-tabs">
          {[
            ['all', 'All', counts.all],
            ['published', 'Published', counts.published],
            ['hidden', 'Hidden', counts.hidden],
            ['reported', 'Reported', counts.reported],
            ['other', 'Other / deleted', counts.other]
          ].map(([key, label, count]) => (
            <button key={key}
              className={tab === key ? 'active' : ''}
              onClick={() => setTab(key)}>
              {label} <span>{count}</span>
            </button>
          ))}
        </div>

        <section className="bsrv-panel">
          <div className="bsrv-filters">
            <label className="bsrv-search">
              Search reviews
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Client, provider, comment or booking" />
            </label>

            <label>
              Provider
              <select value={provider}
                onChange={e => setProvider(e.target.value)}>
                <option value="">All providers</option>
                {providers.map(name =>
                  <option key={name} value={name}>{name}</option>
                )}
              </select>
            </label>

            <label>
              Rating
              <select value={rating} onChange={e => setRating(e.target.value)}>
                <option value="">All ratings</option>
                {[5, 4, 3, 2, 1].map(n =>
                  <option key={n} value={n}>{n} stars</option>
                )}
              </select>
            </label>

            <label>
              Review date
              <select value={dateFrom && dateTo ? 'custom' : dateFrom ? 'from' : dateTo ? 'to' : 'all'}
                onChange={e => { if (e.target.value === 'all') { setDateFrom(''); setDateTo(''); } else if (e.target.value === '30') { const now=new Date(); const start=new Date(now); start.setDate(now.getDate()-29); setDateFrom(start.toISOString().slice(0,10)); setDateTo(now.toISOString().slice(0,10)); } else if(e.target.value === '7') { const now=new Date(); const start=new Date(now); start.setDate(now.getDate()-6); setDateFrom(start.toISOString().slice(0,10)); setDateTo(now.toISOString().slice(0,10)); } }}>
                <option value="all">All dates</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                {(dateFrom || dateTo) && <option value={dateFrom && dateTo ? 'custom' : dateFrom ? 'from' : 'to'}>Custom dates</option>}
              </select>
            </label>
            <label className="bsrv-custom-date">From<input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}/></label>
            <label className="bsrv-custom-date">To<input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}/></label>
            <label className="bsrv-sort">Sort by
              <select value={sort} onChange={e => setSort(e.target.value)}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="rating-high">Highest rating</option>
                <option value="rating-low">Lowest rating</option>
              </select>
            </label>
            <button className="bsrv-reset" onClick={reset}>
              Reset filters
            </button>
          </div>

          <div className="bsrv-list-head">
            <span>{loaded ? `${filtered.length} reviews shown · ${dateFrom || dateTo ? 'Filtered review dates' : 'All review dates'}` : 'Loading reviews…'}</span>
          </div>

          {error && <div className="bsrv-error" role="alert">{error}</div>}

          <div className="bsrv-table-wrap">
            <table>
              <thead><tr><th>Review</th><th>Client / provider</th><th>Review date</th><th>Status</th></tr></thead>
              <tbody>{filtered.map(r => (
                <tr key={r.id}>
                  <td data-label="Review">
                    <div className="bsrv-row-stars" aria-label={`${r.rating ?? 'Unknown'} out of 5 stars`}>
                      {'★'.repeat(Math.max(0,Math.min(5,Math.floor(Number(r.rating)||0))))}
                      {'☆'.repeat(Math.max(0,5-Math.floor(Number(r.rating)||0)))}
                      {Number(r.rating)%1!==0 && <span className="bsrv-numeric-rating">{r.rating}/5</span>}
                    </div>
                    <span className="bsrv-review-comment">{r.comment || 'No written comment'}</span>
                    <button className="bsrv-review-link" onClick={()=>setSelectedId(r.id)}>Review details →</button>
                  </td>
                  <td data-label="Client / provider"><strong>{r.clientName || 'Client unavailable'}</strong><small>{r.providerName || 'Provider unavailable'}</small></td>
                  <td data-label="Review date">{dateLabel(r.createdAt)}</td>
                  <td data-label="Status"><span className={'bsrv-status '+visibility(r).toLowerCase()}>{visibility(r)}</span></td>
                </tr>
              ))}</tbody>
            </table>
            {loaded && !filtered.length && <div className="bsrv-empty">No reviews match the selected filters.</div>}
          </div>
        </section>
      </div>

      {selected && (
        <div className="bsrv-overlay"
          onMouseDown={e => {
            if (e.target === e.currentTarget) setSelectedId(null);
          }}>
          <aside className="bsrv-drawer" role="dialog" aria-modal="true"
            aria-label="Review details">
            <button className="bsrv-close"
              aria-label="Close review details"
              onClick={() => setSelectedId(null)}>×</button>

            <h2>Review details</h2>
            <p>Review #{selected.id}</p>

            <div className="bsrv-stars">
              {'★'.repeat(Math.max(0, Math.min(5, Math.round(Number(selected.rating) || 0))))}
              <strong>{selected.rating ?? '—'} / 5</strong>
            </div>

            <div className="bsrv-comment">
              {selected.comment || 'No written comment provided.'}
            </div>

            <div className="bsrv-detail-grid">
              <div><small>Client</small><strong>{selected.clientName || 'Unavailable'}</strong></div>
              <div><small>Service provider</small><strong>{selected.providerName || 'Unavailable'}</strong></div>
              <div><small>Review submitted</small><strong>{dateLabel(selected.createdAt)}</strong></div>
              <div><small>Appointment date</small><strong>{dateLabel(selected.bookingDate || selected.dateOfBooking)}</strong></div>
              <div><small>Visibility</small><strong>{visibility(selected)}</strong></div>
            </div>

            <h3>Reports</h3>
            {(metadata.reports.filter(r=>String(r.reviewId)===String(selected.id))).map(r=>(
              <div className="bsrv-audit" key={r.id}><strong>{r.reason}</strong><small>{r.adminEmail} · {dateLabel(r.createdAt)}</small><p>{r.explanation}</p></div>
            ))}
            {!metadata.reports.some(r=>String(r.reviewId)===String(selected.id)) && <p className="bsrv-muted">No reports recorded.</p>}
            <h3>Review moderation</h3>
            <label className="bsrv-form-label">Action
              <select value={moderationAction} onChange={e=>setModerationAction(e.target.value)}>
                <option value="hide">Hide review</option><option value="restore">Restore review</option>
              </select>
            </label>
            <label className="bsrv-form-label">Reason (required)
              <select value={reason} onChange={e=>setReason(e.target.value)}>
                <option value="">Select a reason</option>
                {['Personal information','Abusive or threatening content','Spam or promotional content','Review eligibility issue','Other policy concern','Hidden in error','Issue resolved','Report does not justify hiding'].map(v=><option key={v}>{v}</option>)}
              </select>
            </label>
            <label className="bsrv-form-label">Explanation (optional)
              <textarea value={explanation} maxLength={2000} onChange={e=>setExplanation(e.target.value)} rows={3}/>
            </label>
            <div className="bsrv-actions">
              <button className="bsrv-view" disabled={saving || !reason || (moderationAction==='hide' && visibility(selected)!=='Published') || (moderationAction==='restore' && visibility(selected)!=='Hidden')}
                onClick={()=>submitModeration('moderate')}>{saving?'Saving…':moderationAction==='hide'?'Confirm hide':'Confirm restore'}</button>
              <button className="bsrv-outline" disabled={saving||!reason} onClick={()=>submitModeration('report')}>Flag for review</button>
            </div>
            <h3>Moderation history</h3>
            {metadata.history.filter(h=>String(h.reviewId)===String(selected.id)).map(h=>(
              <div className="bsrv-audit" key={h.id}><strong>{h.action==='hide'?'Hidden':'Restored'} · {h.reason}</strong><small>{h.adminEmail} · {dateLabel(h.createdAt)}</small><p>{h.explanation}</p></div>
            ))}
            {!metadata.history.some(h=>String(h.reviewId)===String(selected.id)) && <p className="bsrv-muted">No moderation actions recorded.</p>}
          </aside>
        </div>
      )}
    </main>
  );
}
